import React from "react";

import useEmblaCarousel from "embla-carousel-react";

import FollowButton from "../FollowButton";

const STAFF_KEYWORDS = [
  "Manager",
  "Coach",
  "Owner",
  "Director",
  "Fitness",
  "Assistant",
];
const isStaffPosition = (position) =>
  STAFF_KEYWORDS.some((k) => position?.includes(k));

const StaffCard = ({ person }) => (
  <div className="flex-shrink-0 w-44 bg-white/[0.04] rounded-xl border border-white/[0.08] border-t-2 border-t-amber-500/50 overflow-hidden">
    <div className="relative h-24 bg-gradient-to-b from-white/[0.06] to-transparent overflow-hidden">
      {person.strCutout || person.strThumb ? (
        <img
          alt={person.strPlayer}
          className="w-full h-full object-cover object-top"
          src={person.strCutout || person.strThumb}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <i className="fas fa-user-tie text-white/10 text-3xl" />
        </div>
      )}
    </div>
    <div className="p-3">
      <p className="text-white text-sm font-semibold truncate">
        {person.strPlayer}
      </p>
      <p className="text-amber-400/60 text-xs mt-0.5">{person.strPosition}</p>
      {person.strNationality && (
        <p className="text-white/30 text-xs mt-0.5">{person.strNationality}</p>
      )}
    </div>
  </div>
);

const SquadPositionRail = ({ label, players, teamColors, onPlayerClick }) => {
  const [emblaRef] = useEmblaCarousel({
    dragFree: true,
    containScroll: false,
    align: "start",
  });

  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold text-white mb-3">
        {label}
        <span className="text-white/30 text-sm font-normal ml-2">
          ({players.length})
        </span>
      </h3>
      <div data-carousel className="overflow-visible" ref={emblaRef}>
        <div className="flex gap-4">
          {players.map((player) => (
            <div
              className="flex-shrink-0 w-40 bg-white/[0.04] rounded-xl border border-white/[0.08] overflow-hidden hover:border-blue-500/30 transition-all cursor-pointer group focus:outline-none focus:ring-2 focus:ring-blue-400/50"
              key={player.id || player.name}
              role="button"
              tabIndex={0}
              onClick={() => onPlayerClick(player)}
              onKeyDown={(e) => e.key === "Enter" && onPlayerClick(player)}
            >
              <div className="relative h-28 bg-gradient-to-b from-white/[0.06] to-transparent overflow-hidden">
                {player.thumbUrl || player.photo ? (
                  <img
                    alt={player.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform"
                    src={player.thumbUrl || player.photo}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="fas fa-user text-white/10 text-3xl" />
                  </div>
                )}
                {player.number && (
                  <div
                    className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background: `${teamColors.primary}CC` }}
                  >
                    {player.number}
                  </div>
                )}
              </div>
              <div className="p-3">
                <p className="text-white text-sm font-semibold truncate">
                  {player.name}
                </p>
                <div className="flex items-center gap-2 mt-1 text-xs text-white/40">
                  {player.age && <span>{player.age}y</span>}
                  {player.nationality && <span>{player.nationality}</span>}
                </div>
                <div className="mt-2">
                  <FollowButton
                    entity={{
                      id: player.id,
                      name: player.name,
                      photo: player.thumbUrl || player.photo,
                      position: player.position,
                      teamName: null,
                    }}
                    size="sm"
                    type="player"
                    variant="icon"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TeamDetailSquadTab = ({
  sportsDbPlayers,
  positionGroups,
  loading,
  teamColors,
  onPlayerClick,
}) => {
  const staff = (sportsDbPlayers || []).filter((p) =>
    isStaffPosition(p.strPosition)
  );

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <div className="h-4 w-16 bg-white/10 rounded animate-pulse mb-3" />
          <div className="flex gap-4">
            {[...Array(3)].map((_, i) => (
              <div
                className="w-44 h-44 bg-white/[0.04] rounded-xl border border-white/[0.08] animate-pulse"
                key={i}
              />
            ))}
          </div>
        </div>
        {[...Array(3)].map((_, gi) => (
          <div key={gi}>
            <div className="h-6 w-32 bg-white/10 rounded animate-pulse mb-3" />
            <div className="flex gap-4">
              {[...Array(4)].map((__, pi) => (
                <div
                  className="w-40 h-56 bg-white/[0.04] rounded-xl border border-white/[0.08] animate-pulse"
                  key={pi}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const hasPlayers = Object.keys(positionGroups).length > 0;

  if (!hasPlayers && staff.length === 0) {
    return (
      <div className="text-center py-20">
        <i className="fas fa-users text-5xl text-white/10 mb-4" />
        <p className="text-white/40 text-lg">
          Squad information not available for this team
        </p>
      </div>
    );
  }

  return (
    <div>
      {staff.length > 0 && (
        <div className="mb-8 pb-8 border-b border-white/[0.06]">
          <h3 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4">
            Staff
          </h3>
          <div className="flex flex-wrap gap-4">
            {staff.map((person) => (
              <StaffCard
                key={person.idPlayer || person.strPlayer}
                person={person}
                teamColors={teamColors}
              />
            ))}
          </div>
        </div>
      )}

      {Object.entries(positionGroups).map(([group, players]) => (
        <SquadPositionRail
          key={group}
          label={group === "Other" ? "Other Positions" : `${group}s`}
          players={players}
          teamColors={teamColors}
          onPlayerClick={onPlayerClick}
        />
      ))}
    </div>
  );
};

export default TeamDetailSquadTab;
